/**
 * Main hook that orchestrates data processing for LineChart
 * Combines responsive points calculation and (optional) data processing logic
 */

import { useMemo } from "react";
import { useResponsivePoints, UseResponsivePointsOptions } from "./useResponsivePoints";
import { MAX_POINTS } from "../utils/responsivePoints";

export interface UseChartDataProcessingOptions {
  data: any[];
  downsample?: boolean;
  maxPoints?: number; // Explicit maxPoints (takes precedence over responsive)
  interval?: string;
  xAxisPath?: string;
  responsive?: boolean;
  containerRef: UseResponsivePointsOptions["containerRef"];
  responsiveConfig?: { minPoints?: number; maxPoints?: number };
}

export interface UseChartDataProcessingResult {
  processedData: any[];
  maxXTicks?: number;
}

export const useChartDataProcessing = (
  options: UseChartDataProcessingOptions
): UseChartDataProcessingResult => {
  const {
    data,
    downsample = true,
    maxPoints: explicitMaxPoints,
    interval,
    xAxisPath,
    responsive = true,
    containerRef,
    responsiveConfig = {},
  } = options;

  // Calculate responsive max points if enabled
  const responsiveMaxPoints = useResponsivePoints({
    enabled: responsive && downsample && !explicitMaxPoints,
    containerRef,
    ...responsiveConfig,
  });

  // Determine the final maxPoints to use
  const maxXTicks = useMemo(() => {
    if (!downsample) return undefined;
    
    // Explicit maxPoints takes precedence
    if (explicitMaxPoints !== undefined) {
      return explicitMaxPoints;
    }
    
    // Use responsive calculation if available
    if (responsiveMaxPoints !== undefined) {
      return responsiveMaxPoints;
    }
    
    // Default to MAX_POINTS if neither is available
    return MAX_POINTS;
  }, [downsample, explicitMaxPoints, responsiveMaxPoints]);

  // Ticks-only downsampling: keep full dataset for rendering/tooltip accuracy.
  // NOTE: interval/xAxisPath are intentionally unused here; they’re used for tick selection in the chart.
  void interval;
  void xAxisPath;

  return { processedData: data, maxXTicks };
};
